import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [aiToolsOpen, setAiToolsOpen] = useState(false);
  const [mobileAiToolsOpen, setMobileAiToolsOpen] = useState(false);
  const location = useLocation();
  // Hide the signup CTAs while the user is already on a signup form page.
  const isSignupPage = location.pathname === "/apply" || location.pathname === "/buyer";
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/offers", label: "PPC Offers" },
    { path: "/leads-generation", label: "Ad Management" },
    { path: "/learn", label: "Courses" },
    { path: "/faq", label: "FAQ" },
    { path: "/careers", label: "Careers" },
    { path: "/contact", label: "Contact" }
  ];

  // AI Developers mega-menu — left column first, then right column.
  const servicesItems = [
    { label: "Website Development", path: "/website-development" },
    { label: "Custom Software", path: "/services/custom-software" },
    { label: "AI Software", path: "/services/ai-software" },
    { label: "CRM Development", path: "/services/crm-development" },
    { label: "Business Automation", path: "/services/business-automation" },
  ];
  const servicesMid = Math.ceil(servicesItems.length / 2);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-slate-900 ${
      scrolled ? 'shadow-lg shadow-slate-900/50' : ''
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="header-logo">
            <img 
              src="/logo_no_bg.png" 
              alt="Digital Master Media LLC" 
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </Link>
          
          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center justify-center flex-1 px-4" data-testid="desktop-nav">
            <div className="flex items-center gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`relative px-2.5 py-2 text-[13px] font-medium rounded-md transition-all duration-200 whitespace-nowrap ${
                    isActive(link.path) 
                      ? 'text-white bg-white/10' 
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-blue-400 rounded-full" />
                  )}
                </Link>
              ))}
              
              {/* AI Developers Mega-Menu Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setAiToolsOpen(true)}
                onMouseLeave={() => setAiToolsOpen(false)}
              >
                <button
                  className="flex items-center gap-1 px-2.5 py-2 text-[13px] font-medium rounded-md transition-all duration-200 text-slate-300 hover:text-white hover:bg-white/5 whitespace-nowrap"
                  data-testid="nav-link-services"
                >
                  AI Developers
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${aiToolsOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Mega-Menu — two columns, dark midnight theme */}
                {aiToolsOpen && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[30rem] bg-slate-800/95 backdrop-blur-xl border border-slate-700 rounded-xl shadow-2xl p-4 z-50"
                    data-testid="services-mega-menu"
                  >
                    <div className="grid grid-cols-2 gap-x-2">
                      {[servicesItems.slice(0, servicesMid), servicesItems.slice(servicesMid)].map((col, colIdx) => (
                        <div key={colIdx} className="space-y-0.5">
                          {col.map((item) => (
                            <Link
                              key={item.label}
                              to={item.path}
                              onClick={() => setAiToolsOpen(false)}
                              className="group flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-blue-600/15 transition-colors"
                              data-testid={`service-link-${item.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500/60 group-hover:bg-blue-400 flex-shrink-0 transition-colors" />
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </nav>
          
          {/* CTA Buttons — hidden on the signup form pages themselves */}
          {!isSignupPage && (
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <Link
              to="/apply"
              data-testid="header-publisher-btn"
              className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold px-5 py-2.5 rounded-lg shadow-md hover:shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
            >
              <span className="relative z-10">Publisher Sign Up</span>
            </Link>
            <Link
              to="/buyer"
              data-testid="header-buyer-btn"
              className="font-semibold px-5 py-2.5 rounded-lg border-2 border-slate-600 text-slate-300 hover:border-blue-400 hover:text-white hover:bg-white/5 transition-all duration-200"
            >
              Buyer Sign Up
            </Link>
          </div>
          )}
          
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 rounded-lg transition-colors text-slate-300 hover:bg-white/10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-slate-800 border-t border-slate-700" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive(link.path)
                    ? 'text-white bg-white/10'
                    : 'text-slate-300 hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Mobile AI Developers Dropdown */}
            <div>
              <button
                onClick={() => setMobileAiToolsOpen(!mobileAiToolsOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-white/5 transition-all"
                data-testid="mobile-nav-services"
              >
                AI Developers
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAiToolsOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileAiToolsOpen && (
                <div className="ml-4 mt-1 space-y-1">
                  {servicesItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-blue-600/15 rounded-lg transition-colors"
                      data-testid={`mobile-service-link-${item.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500/60 flex-shrink-0" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            {!isSignupPage && (
            <div className="pt-4 space-y-2 border-t border-slate-700 mt-4">
              <Link
                to="/apply"
                onClick={() => setMobileMenuOpen(false)}
                className="block bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-3 px-4 rounded-lg text-center"
              >
                Publisher Sign Up
              </Link>
              <Link
                to="/buyer"
                onClick={() => setMobileMenuOpen(false)}
                className="block border-2 border-slate-600 text-slate-300 font-semibold py-3 px-4 rounded-lg text-center hover:border-blue-400 hover:text-white"
              >
                Buyer Sign Up
              </Link>
            </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
